import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Layout, SupportIcon } from "lola-framework-ui-test";
import { BodyCopy } from "../components/BodyCopy";
import { AuraLayout } from "../components/AuraLayout";
import { TransactionsHistory } from "../components/TransactionsHistory";
import { APP_ROUTES } from "../constants";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useComposeStore } from "../store/useComposeStore";
import { useExitToPartnerOrLanding } from "../hooks/useExitToPartnerOrLanding";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { usePullToRefresh } from "../hooks/usePullToRefresh";
import {
  useOperationSSE,
  upsertOperationInList,
} from "../hooks/useOperationSSE";
import { listOperations } from "../services/api";
import type { IOperationResponse } from "../services/api";
import { amountFormatter, getCurrentDateFormatted } from "../utils";
import { isCashDepositPending } from "../utils/cashDeposit";
import { useLaunchCashDepositPayment } from "../hooks/useLaunchCashDepositPayment";
import type { ITransaction } from "../interfaces/transactions";
import { getEvents, safeCapture } from "../analytics";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { NotificationsBell } from "../components/notifications/NotificationsBell";
import { useNovuInbox } from "../components/notifications/NovuInboxProvider";
import { Navbar } from "../components/Navbar";

const PAGE_SIZE = 8;
const PULL_THRESHOLD = 70;

const formatDate = (isoDate?: string): string => {
  if (!isoDate) return getCurrentDateFormatted();
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return getCurrentDateFormatted();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear().toString().slice(-2);
  return `${day}/${month}/${year}`;
};

/** Recipient full name from the operation party snapshot, if present. */
const recipientLabel = (op: IOperationResponse): string | undefined => {
  const name = [op.recipient?.name?.firstName, op.recipient?.name?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || undefined;
};

/** Display name for a cash-deposit row: recipient name, else send amount. */
const cashDepositLabel = (op: IOperationResponse): string => {
  return (
    recipientLabel(op) ||
    `$${amountFormatter(op.sendAmount, op.sourceCurrency)} ${op.sourceCurrency}`
  );
};

/**
 * Send hub. Lists the sender's operations (most recent first) with
 * pull-to-refresh, infinite scroll, and live SSE status updates.
 */
export const HomePage = memo(() => {
  const { t } = useTranslation();
  const { theme, setAmountState } = useStore();
  const { session } = useSessionStore();
  const { resetCompose } = useComposeStore();
  const { redirectTo, linkId: linkIdFromRoute } = useRouteWithToken();
  const { exitToPartnerOrLanding, canExitToPartner } =
    useExitToPartnerOrLanding();

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("home"));
  }, []);

  const userStatus = session.data?.userStatus;
  const canSend = !userStatus?.blocked && !userStatus?.suspended;

  const startNewSend = useCallback(() => {
    if (!canSend) return;
    resetCompose();
    setAmountState({ amount: "", anchor: "send" });
    redirectTo(APP_ROUTES.TRANSFER_PAGE);
  }, [canSend, resetCompose, setAmountState, redirectTo]);

  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, dropzoneColors, bodyCopy } = colorConfig;

  const linkId = linkIdFromRoute || session.data?.linkId;

  const [operations, setOperations] = useState<IOperationResponse[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [payingOperationId, setPayingOperationId] = useState<string | null>(
    null,
  );

  const pageRef = useRef(0);
  const loadingRef = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const { launch: launchCashDeposit, launching: launchingCashDeposit } =
    useLaunchCashDepositPayment(linkId);

  const handleDeposit = useCallback(
    async (operationId: string) => {
      safeCapture(getEvents().CASH_DEPOSIT_LAUNCH, {
        operation_id: operationId,
      });
      setPayingOperationId(operationId);
      try {
        await launchCashDeposit(operationId);
      } finally {
        setPayingOperationId(null);
      }
    },
    [launchCashDeposit],
  );

  const mapOperation = useCallback(
    (o: IOperationResponse): ITransaction => {
      const recipientName = recipientLabel(o);
      const base: ITransaction = {
        operationId: o.operationId,
        status: o.status,
        amount: amountFormatter(o.sendAmount, o.sourceCurrency),
        currency: o.sourceCurrency,
        date: formatDate(o.createdAt),
        recipientName,
      };
      if (isCashDepositPending(o)) {
        return {
          ...base,
          cashDepositPending: true,
          depositName: cashDepositLabel(o),
          launching:
            launchingCashDeposit && payingOperationId === o.operationId,
          onDeposit: () => {
            void handleDeposit(o.operationId);
          },
        };
      }
      return {
        ...base,
        onSelect: () =>
          redirectTo(
            APP_ROUTES.RECEIPT_SUMMARY_PAGE.replace(
              ":operationId",
              o.operationId
            )
          ),
      };
    },
    [redirectTo, handleDeposit, launchingCashDeposit, payingOperationId]
  );

  const transactions = useMemo<ITransaction[]>(
    () => operations.map(mapOperation),
    [operations, mapOperation]
  );

  const loadNextPage = useCallback(async () => {
    if (!linkId || loadingRef.current) return;

    loadingRef.current = true;
    const nextPage = pageRef.current + 1;

    try {
      const { items, pagination } = await listOperations(linkId, {
        page: nextPage,
        limit: PAGE_SIZE,
      });

      setOperations((prev) => (nextPage === 1 ? items : [...prev, ...items]));
      pageRef.current = nextPage;
      setHasMore(pagination ? pagination.hasMore : false);
    } catch (error) {
      console.error("[HomePage] Failed to fetch operations:", error);
      setHasMore(false);
    } finally {
      loadingRef.current = false;
    }
  }, [linkId]);

  const refresh = useCallback(async () => {
    pageRef.current = 0;
    setHasMore(true);
    await loadNextPage();
  }, [loadNextPage]);

  const upsertOperation = useCallback((op: IOperationResponse) => {
    setOperations((prev) => upsertOperationInList(prev, op));
  }, []);

  const reconcileFromServer = useCallback(async () => {
    if (!linkId) return;
    try {
      const { items } = await listOperations(linkId, {
        page: 1,
        limit: PAGE_SIZE,
      });
      setOperations((prev) =>
        items.reduce((acc, op) => upsertOperationInList(acc, op), prev)
      );
    } catch (error) {
      console.error("[HomePage] SSE reconnect reconcile failed:", error);
    }
  }, [linkId]);

  useOperationSSE(linkId, operations, upsertOperation, reconcileFromServer);

  const { pullDistance, refreshing, pulling } = usePullToRefresh(
    containerRef,
    refresh,
    { threshold: PULL_THRESHOLD }
  );

  useEffect(() => {
    if (linkId && pageRef.current === 0) {
      void loadNextPage();
    }
  }, [linkId, loadNextPage]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) void loadNextPage();
      },
      { root: containerRef.current, rootMargin: "120px", threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, transactions.length, loadNextPage]);

  const { openSupport, supportSheet } = useSupportSheet();
  const inbox = useNovuInbox();
  const headerSlot = inbox?.headerSlot ?? "help";
  const showBell = headerSlot === "bell";
  const showHelp = headerSlot === "help";

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className={
          showBell
            ? "need-help-header notifications-home-header"
            : "need-help-header"
        }
      >
        <Layout.Header>
          {headerSlot === "pending" ? null : (
            <Navbar
              title={showHelp ? t("common.needHelp") : undefined}
              align="right"
              onBackClick={() => redirectTo(APP_ROUTES.LANDING_PAGE)}
              onIconClick={showHelp ? () => openSupport() : undefined}
              icon={
                showBell ? (
                  <NotificationsBell />
                ) : (
                  <SupportIcon size={35} colors={dropzoneColors} />
                )
              }
            />
          )}
        </Layout.Header>
        <Layout.Content className="transactions-section">
          <TransactionsHistory
            items={transactions}
            containerRef={containerRef}
            sentinelRef={sentinelRef}
            hasMore={hasMore}
            pullDistance={pullDistance}
            refreshing={refreshing}
            pulling={pulling}
            pullThreshold={PULL_THRESHOLD}
          />
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section transactions-footer">
            {!canSend && (
              <BodyCopy
                textColor={bodyCopy}
                className="transactions-disabled-note"
              >
                {t("transactions.sendsDisabled")}
              </BodyCopy>
            )}
            {canExitToPartner && (
              <Button
                size="small"
                variant="cancel"
                background={backgroundBtn}
                color={backgroundBtn}
                className="alreary-acc-btn"
                onClick={exitToPartnerOrLanding}
              >
                {t("transactions.backHome")}
              </Button>
            )}
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              disabled={!canSend}
              onClick={startNewSend}
            >
              {t("transactions.newSend")}
            </Button>
          </section>
        </Layout.Footer>
      </AuraLayout>
      {supportSheet}
    </>
  );
});
